import { useState } from "react";
import { TrendingDown } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { Reveal, Eyebrow } from "./Reveal";

const IN_HOUSE_PER_PROVIDER = 48000 * 1.24;

const STATS = [
  { value: "70%", label: "Lower admin overhead" },
  { value: "48 hr", label: "Average onboarding time" },
  { value: "99.4%", label: "Client retention rate" },
  { value: "500+", label: "US practices supported" },
];

const fmt = (n) => "$" + Math.round(n).toLocaleString("en-US");

export const ROICalculator = () => {
  const [providers, setProviders] = useState(3);
  const inHouse = providers * IN_HOUSE_PER_PROVIDER;
  const savings = inHouse * 0.7;

  return (
    <section id="savings" className="py-20 lg:py-28" data-testid="savings-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <Eyebrow>Practice Savings</Eyebrow>
          <h2 className="mt-4 max-w-2xl font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight tracking-tight text-[#0F1E19]">
            See what in-house staffing is really costing you.
          </h2>
        </Reveal>

        <div className="mt-12 grid lg:grid-cols-2 gap-8">
          <Reveal delay={0.1}>
            <div className="h-full rounded-3xl border border-[#E2E0D5] bg-white p-8 sm:p-10" data-testid="roi-calculator">
              <div className="flex items-baseline justify-between">
                <label className="text-sm font-semibold text-[#0F1E19]">Providers in your practice</label>
                <span className="font-mono text-3xl font-semibold text-[#0A3B2C]" data-testid="roi-provider-count">
                  {providers}
                </span>
              </div>
              <div className="mt-6">
                <Slider
                  data-testid="roi-provider-slider"
                  min={1}
                  max={20}
                  step={1}
                  value={[providers]}
                  onValueChange={(v) => setProviders(v[0])}
                />
              </div>
              <div className="mt-2 flex justify-between font-mono text-[11px] text-[#4A5D55]">
                <span>1 provider</span>
                <span>20 providers</span>
              </div>

              <div className="mt-8 space-y-4">
                <div className="flex items-center justify-between rounded-2xl bg-[#F3F2EB] px-5 py-4">
                  <span className="text-sm text-[#4A5D55]">In-house MAs (salary + benefits)</span>
                  <span className="font-mono text-lg font-semibold text-[#0F1E19]" data-testid="roi-inhouse-cost">
                    {fmt(inHouse)}/yr
                  </span>
                </div>
                <div className="flex items-center justify-between rounded-2xl bg-[#E6F4EE] px-5 py-4">
                  <span className="text-sm text-[#0A3B2C]">Verma Med Virtual MAs</span>
                  <span className="font-mono text-sm font-semibold text-[#0A3B2C]" data-testid="roi-vma-cost">
                    Tailored quote
                  </span>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.2}>
            <div className="flex h-full flex-col justify-between rounded-3xl bg-[#0A261E] p-8 sm:p-10" data-testid="roi-savings-card">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-white/15 px-3 py-1.5">
                  <TrendingDown className="h-3.5 w-3.5 text-[#EAB308]" />
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#A3B8B0]">
                    Projected annual savings
                  </span>
                </div>
                <p className="mt-6 font-mono text-xs uppercase tracking-[0.2em] text-[#EAB308]">Up to</p>
                <p className="mt-1 font-display text-5xl sm:text-6xl font-extrabold tracking-tight text-[#F5F7F6]" data-testid="roi-savings-amount">
                  {fmt(savings)}
                </p>
                <p className="mt-3 max-w-sm text-sm leading-relaxed text-[#A3B8B0]">
                  Practices switching to virtual MAs typically cut up to 70% of in-house admin staffing cost — money
                  back into new equipment, another provider, or simply your weekends.
                </p>
              </div>
              <p className="mt-8 font-mono text-xs text-[#A3B8B0]/70">
                Estimate based on $48,000 avg MA salary + 24% benefits. Your exact Verma Med rate is shared after a free consultation.
              </p>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <div className="mt-16 grid grid-cols-2 lg:grid-cols-4" data-testid="stats-strip">
            {STATS.map((s, i) => (
              <div
                key={s.label}
                className={`px-4 py-6 sm:px-8 ${i > 0 ? "border-l border-[#0F1E19]/10" : ""}`}
                data-testid={`stat-${i}`}
              >
                <p className="font-mono text-3xl sm:text-4xl font-semibold text-[#0A3B2C]">{s.value}</p>
                <p className="mt-2 text-xs sm:text-sm text-[#4A5D55]">{s.label}</p>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
